import {
  UpdateWorkplaceExternalIntegrationRequestSchema,
  UpdateWorkplaceExternalIntegrationResponseSchema,
} from "@clipboard-health/contract-backend-main";
import { patch } from "@src/appV2/api/parsedApi";
import { getWorkplaceExternalIntegrationsUrl } from "@src/appV2/ExternalIntegrations/api/useGetWorkplaceExternalIntegrations";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { type z } from "zod";

type UpdateWorkplaceExternalIntegrationRequest = z.input<
  typeof UpdateWorkplaceExternalIntegrationRequestSchema
>;

interface UseUpdateWorkplaceExternalIntegrationParams {
  workplaceId: string;
  externalIntegrationId: string;
  attributes: UpdateWorkplaceExternalIntegrationRequest["data"]["attributes"];
}

export function useUpdateWorkplaceExternalIntegration() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: UseUpdateWorkplaceExternalIntegrationParams) => {
      const { workplaceId, externalIntegrationId, attributes } = params;

      const response = await patch({
        url: `${getWorkplaceExternalIntegrationsUrl(workplaceId)}/${externalIntegrationId}`,
        data: {
          data: {
            type: "workplace-external-integration" as const,
            attributes,
          },
        },
        bodySchema: UpdateWorkplaceExternalIntegrationRequestSchema,
        responseSchema: UpdateWorkplaceExternalIntegrationResponseSchema,
      });

      return response.data;
    },
    onSuccess: (_data, params) => {
      void queryClient.invalidateQueries({
        queryKey: [getWorkplaceExternalIntegrationsUrl(params.workplaceId)],
      });
    },
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.UPDATE_WORKPLACE_EXTERNAL_INTEGRATION_FAILURE,
    },
  });
}
